import { DeleteFilled, InfoCircleOutlined, PictureOutlined } from "@ant-design/icons";
import { Button, Form, Input, InputNumber, Switch } from "antd";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import customizeRequiredMark from "../../../../components/custom_antd/CustomizeRequiredMark";

const RATE_FIELDS = [
    {
        key: "hourly",
        labelKey: "rates.perHour",
        toggleName: "disableHourly",
        rateName: "hourlyRate",
        tooltip: "Set the rental price for each hour the vehicle is booked.",
    },
    {
        key: "daily",
        labelKey: "rates.perDay",
        toggleName: "disableDaily",
        rateName: "dailyRate",
        tooltip: "Set the rental price for a full 24-hour period.",
    },
    {
        key: "weekly",
        labelKey: "rates.perWeek",
        toggleName: "disableWeekly",
        rateName: "weeklyRate",
        tooltip: "Set a discounted rate for weekly rentals (7 consecutive days).",
    },
    {
        key: "monthly",
        labelKey: "rates.perMonth",
        toggleName: "disableMonthly",
        rateName: "monthlyRate",
        tooltip: "Set a discounted rate for monthly rentals (30 consecutive days).",
    },
];

const inputClassName = "rounded-lg";

const CategoryFormFields = ({
    form,
    onFinish,
    isCarPricingMode,
    previewUrl,
    imageValidation,
    fileInputRef,
    onFileChange,
    onClearImage,
    rateToggles,
    onRateToggle,
}) => {
    const { t } = useTranslation("cars");

    return (
        <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            colon={false}
            requiredMark={customizeRequiredMark}
            className="[&_.ant-form-item]:mb-4 [&_.ant-form-item-label>label]:text-sm [&_.ant-form-item-label>label]:font-medium [&_.ant-form-item-label>label]:text-gray-700">
            <div className="rounded-xl border border-gray-100 bg-gray-50/60 p-4">
                <p className="mb-4 text-sm font-semibold text-gray-900">{t("rates.basicInfo")}</p>

                <div
                    className={`grid gap-4 ${
                        isCarPricingMode
                            ? "grid-cols-1"
                            : "grid-cols-1 sm:grid-cols-[9rem_minmax(0,1fr)]"
                    }`}>
                    <div
                        className={`flex flex-col gap-2 ${
                            isCarPricingMode ? "items-center" : "items-center sm:items-start"
                        }`}>
                        <input
                            ref={fileInputRef}
                            type="file"
                            onChange={onFileChange}
                            accept="image/*"
                            className="!m-0 !h-0 !w-0 !overflow-hidden !border-0 !p-0 opacity-0"
                            tabIndex={-1}
                            aria-hidden
                        />
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className={`relative h-36 w-36 shrink-0 overflow-hidden rounded-xl border-2 border-dashed transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-200 ${
                                previewUrl
                                    ? "border-indigo-400 bg-white"
                                    : imageValidation
                                      ? "border-red-400 bg-red-50/40"
                                      : "border-gray-200 bg-white hover:border-indigo-300"
                            }`}>
                            {previewUrl ? (
                                <img src={previewUrl} alt="" className="h-full w-full object-cover" />
                            ) : (
                                <div className="flex h-full flex-col items-center justify-center gap-2 px-3 text-gray-400">
                                    <PictureOutlined className="text-2xl" />
                                    <span className="text-center text-xs leading-snug">
                                        {t("rates.clickToUpload")}
                                    </span>
                                </div>
                            )}
                        </button>
                        {previewUrl ? (
                            <Button
                                size="small"
                                danger
                                ghost
                                icon={<DeleteFilled />}
                                onClick={onClearImage}
                                className="text-xs">
                                Remove
                            </Button>
                        ) : null}
                        {imageValidation ? (
                            <p className="max-w-[9rem] text-center text-xs text-red-500">
                                {t("rates.pleaseUploadValidImage")}
                            </p>
                        ) : null}
                    </div>

                    <div className={`min-w-0 ${isCarPricingMode ? "" : "sm:pt-1"}`}>
                        <Form.Item
                            name="category"
                            label={t("rates.categoryName")}
                            className="mb-0"
                            rules={[{ required: true, message: t("rates.categoryNameRequired") }]}>
                            <Input size="large" className={inputClassName} placeholder={t("rates.categoryName")} />
                        </Form.Item>
                    </div>
                </div>

                {!isCarPricingMode ? (
                    <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <Form.Item
                            name="seats"
                            label={t("seats")}
                            className="mb-0"
                            rules={[{ required: true, message: t("rates.seatsRequired") }]}>
                            <InputNumber className={`w-full ${inputClassName}`} size="large" min={1} />
                        </Form.Item>
                        <Form.Item
                            name="bags"
                            label={t("rates.bags")}
                            className="mb-0"
                            required
                            rules={[
                                {
                                    validator: (_, value) =>
                                        value === undefined || value === null
                                            ? Promise.reject(new Error(t("rates.bagsRequired")))
                                            : Promise.resolve(),
                                },
                            ]}>
                            <InputNumber className={`w-full ${inputClassName}`} size="large" min={0} />
                        </Form.Item>
                    </div>
                ) : null}
            </div>

            {!isCarPricingMode ? (
                <div className="mt-4 rounded-xl border border-gray-100 bg-gray-50/60 p-4">
                    <p className="mb-4 text-sm font-semibold text-gray-900">{t("rates.setPrice")}</p>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                        {RATE_FIELDS.map(({ key, labelKey, toggleName, rateName, tooltip }) => {
                            const enabled = rateToggles[key];

                            return (
                                <div
                                    key={key}
                                    className="rounded-xl border border-white bg-white p-3 shadow-sm ring-1 ring-gray-100">
                                    <div className="mb-2 flex items-center justify-between gap-2">
                                        <span className="text-xs font-semibold text-gray-700">{t(labelKey)}</span>
                                        <Form.Item
                                            name={toggleName}
                                            valuePropName="checked"
                                            className="mb-0"
                                            colon={false}
                                            tooltip={{ title: tooltip, icon: <InfoCircleOutlined /> }}
                                            labelCol={{ onClick: (e) => e.preventDefault() }}>
                                            <Switch
                                                checkedChildren="On"
                                                unCheckedChildren="Off"
                                                onChange={(checked) => onRateToggle(key, checked)}
                                                size="small"
                                            />
                                        </Form.Item>
                                    </div>
                                    <Form.Item
                                        name={rateName}
                                        rules={[{ required: enabled, message: t("rates.required") }]}
                                        className="mb-0">
                                        <InputNumber
                                            prefix="$"
                                            className={`w-full ${inputClassName}`}
                                            disabled={!enabled}
                                            min={1}
                                            placeholder="0"
                                        />
                                    </Form.Item>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ) : null}
        </Form>
    );
};

CategoryFormFields.propTypes = {
    form: PropTypes.object.isRequired,
    onFinish: PropTypes.func.isRequired,
    isCarPricingMode: PropTypes.bool.isRequired,
    previewUrl: PropTypes.string,
    imageValidation: PropTypes.bool.isRequired,
    fileInputRef: PropTypes.object.isRequired,
    onFileChange: PropTypes.func.isRequired,
    onClearImage: PropTypes.func.isRequired,
    rateToggles: PropTypes.shape({
        hourly: PropTypes.bool,
        daily: PropTypes.bool,
        weekly: PropTypes.bool,
        monthly: PropTypes.bool,
    }).isRequired,
    onRateToggle: PropTypes.func.isRequired,
};

export default CategoryFormFields;
